import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import MultiSelectDropdown from '@/components/MultiSelectDropdown';

function formatTaxonomyLabel(value) {
  return String(value || '')
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

export default function GenreTaxonomySelects({
  genre,
  subgenre,
  microgenre,
  onChange,
  disabled = false,
  prefix = 'taxonomy',
  includeAll = false,
  multiple = false,
}) {
  const [taxonomy, setTaxonomy] = useState({
    genres: [],
    subgenres_by_genre: {},
    microgenres_by_genre: {},
  });

  useEffect(() => {
    let cancelled = false;

    api.get('/labels/filters')
      .then((r) => {
        if (cancelled) return;
        setTaxonomy(r.data || {
          genres: [],
          subgenres_by_genre: {},
          microgenres_by_genre: {},
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const singleSubgenres = useMemo(
    () => taxonomy.subgenres_by_genre?.[genre] || [],
    [taxonomy, genre]
  );

  const singleMicrogenres = useMemo(
    () => taxonomy.microgenres_by_genre?.[genre]?.[subgenre] || [],
    [taxonomy, genre, subgenre]
  );

  const selectedGenres = Array.isArray(genre) ? genre : [];
  const selectedSubgenres = Array.isArray(subgenre) ? subgenre : [];

  const multipleSubgenres = useMemo(() => {
    const values = [];

    selectedGenres.forEach((selectedGenre) => {
      values.push(
        ...(taxonomy.subgenres_by_genre?.[selectedGenre] || [])
      );
    });

    return unique(values);
  }, [taxonomy, selectedGenres]);

  const multipleMicrogenres = useMemo(() => {
    const values = [];

    selectedGenres.forEach((selectedGenre) => {
      selectedSubgenres.forEach((selectedSubgenre) => {
        values.push(
          ...(taxonomy.microgenres_by_genre?.[selectedGenre]?.[
            selectedSubgenre
          ] || [])
        );
      });
    });

    return unique(values);
  }, [taxonomy, selectedGenres, selectedSubgenres]);

  if (multiple) {
    const genreOptions = taxonomy.genres.map((item) => ({
      value: item,
      label: formatTaxonomyLabel(item),
    }));

    const subgenreOptions = multipleSubgenres.map((item) => ({
      value: item,
      label: formatTaxonomyLabel(item),
    }));

    const microgenreOptions = multipleMicrogenres.map((item) => ({
      value: item,
      label: formatTaxonomyLabel(item),
    }));

    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MultiSelectDropdown
          label="Genres"
          options={genreOptions}
          value={selectedGenres}
          onChange={(nextGenres) => {
            const validSubgenres = new Set(
              nextGenres.flatMap(
                (selectedGenre) =>
                  taxonomy.subgenres_by_genre?.[selectedGenre] || []
              )
            );

            const nextSubgenres = selectedSubgenres.filter((item) =>
              validSubgenres.has(item)
            );

            const validMicrogenres = new Set();

            nextGenres.forEach((selectedGenre) => {
              nextSubgenres.forEach((selectedSubgenre) => {
                (
                  taxonomy.microgenres_by_genre?.[selectedGenre]?.[
                    selectedSubgenre
                  ] || []
                ).forEach((item) => validMicrogenres.add(item));
              });
            });

            const nextMicrogenres = (
              Array.isArray(microgenre) ? microgenre : []
            ).filter((item) => validMicrogenres.has(item));

            onChange({
              genre: nextGenres,
              subgenre: nextSubgenres,
              microgenre: nextMicrogenres,
            });
          }}
          max={3}
          placeholder="Select genres"
          testid={`${prefix}-genre`}
        />

        <MultiSelectDropdown
          label="Subgenres"
          options={subgenreOptions}
          value={selectedSubgenres}
          onChange={(nextSubgenres) => {
            const validMicrogenres = new Set();

            selectedGenres.forEach((selectedGenre) => {
              nextSubgenres.forEach((selectedSubgenre) => {
                (
                  taxonomy.microgenres_by_genre?.[selectedGenre]?.[
                    selectedSubgenre
                  ] || []
                ).forEach((item) => validMicrogenres.add(item));
              });
            });

            const currentMicrogenres = Array.isArray(microgenre)
              ? microgenre
              : [];

            onChange({
              genre: selectedGenres,
              subgenre: nextSubgenres,
              microgenre: currentMicrogenres.filter((item) =>
                validMicrogenres.has(item)
              ),
            });
          }}
          max={3}
          placeholder={
            selectedGenres.length ? 'Select subgenres' : 'Select genres first'
          }
          testid={`${prefix}-subgenre`}
        />

        <MultiSelectDropdown
          label="Microgenres"
          options={microgenreOptions}
          value={Array.isArray(microgenre) ? microgenre : []}
          onChange={(nextMicrogenres) =>
            onChange({
              genre: selectedGenres,
              subgenre: selectedSubgenres,
              microgenre: nextMicrogenres,
            })
          }
          max={3}
          placeholder={
            selectedSubgenres.length
              ? 'Select microgenres'
              : 'Select subgenres first'
          }
          testid={`${prefix}-microgenre`}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <label className="font-mono text-[10px] uppercase text-zinc-500">
          Genre
        </label>
        <Select
          value={genre || undefined}
          disabled={disabled}
          onValueChange={(value) =>
            onChange({
              genre: value === '__all__' ? '' : value,
              subgenre: '',
              microgenre: '',
            })
          }
        >
          <SelectTrigger
            data-testid={`${prefix}-genre`}
            className="mt-1 bg-zinc-950 border-zinc-800"
          >
            <SelectValue placeholder="Select genre" />
          </SelectTrigger>
          <SelectContent>
            {includeAll && (
              <SelectItem value="__all__">All genres</SelectItem>
            )}
            {taxonomy.genres.map((item) => (
              <SelectItem key={item} value={item}>
                {formatTaxonomyLabel(item)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase text-zinc-500">
          Subgenre
        </label>
        <Select
          value={subgenre || '__none__'}
          disabled={disabled || !genre}
          onValueChange={(value) =>
            onChange({
              genre,
              subgenre: value === '__none__' ? '' : value,
              microgenre: '',
            })
          }
        >
          <SelectTrigger
            data-testid={`${prefix}-subgenre`}
            className="mt-1 bg-zinc-950 border-zinc-800"
          >
            <SelectValue
              placeholder={genre ? 'Select subgenre' : 'Select genre first'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No subgenre</SelectItem>
            {singleSubgenres.map((item) => (
              <SelectItem key={item} value={item}>
                {formatTaxonomyLabel(item)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="font-mono text-[10px] uppercase text-zinc-500">
          Microgenre
        </label>
        <Select
          value={microgenre || '__none__'}
          disabled={disabled || !subgenre}
          onValueChange={(value) =>
            onChange({
              genre,
              subgenre,
              microgenre: value === '__none__' ? '' : value,
            })
          }
        >
          <SelectTrigger
            data-testid={`${prefix}-microgenre`}
            className="mt-1 bg-zinc-950 border-zinc-800"
          >
            <SelectValue
              placeholder={subgenre ? 'Select microgenre' : 'Select subgenre first'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No microgenre</SelectItem>
            {singleMicrogenres.map((item) => (
              <SelectItem key={item} value={item}>
                {formatTaxonomyLabel(item)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
